import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function Login() {
  const { user, signIn, loading } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/set-password'
      });
      if (error) throw error;
      setSuccess(`Password reset email sent to ${email}. Check your inbox.`);
    } catch (err) {
      setError(err.message || 'Could not send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  const Brand = () => (
    <div style={{ textAlign: 'center', marginBottom: 48 }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 56, height: 56, borderRadius: 14,
        background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
        marginBottom: 20
      }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: '#0a0a0a' }}>H</span>
      </div>
      <h1 style={{ fontSize: 28, color: 'var(--text-primary)', marginBottom: 8 }}>HMA Portal</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Howden Movement Academy</p>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px', position: 'relative', overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(201,169,110,0.06) 0%, transparent 60%)',
        pointerEvents: 'none'
      }} />

      <div style={{ width: '100%', maxWidth: 400, position: 'relative' }}>
        <Brand />

        {mode === 'login' ? (
          <>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 32 }}>
              <h2 style={{ fontSize: 20, marginBottom: 8, color: 'var(--text-primary)' }}>Welcome back</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 28 }}>
                Sign in to your account to continue
              </p>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="label">Email address</label>
                  <input type="email" className="input" placeholder="you@example.com"
                    style={{ fontSize: 16 }}
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="label">Password</label>
                  <input type="password" className="input" placeholder="••••••••"
                    style={{ fontSize: 16 }}
                    value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
                {/* Forgot password link */}
                <div style={{ textAlign: 'right', marginBottom: 20, marginTop: -8 }}>
                  <span
                    onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                    style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer' }}
                  >
                    Forgot password?
                  </span>
                </div>
                <button type="submit" className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 15 }}
                  disabled={submitting}>
                  {submitting ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            </div>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 24 }}>
              Don't have an account? Contact your HMA administrator.
            </p>
          </>
        ) : (
          <>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 32 }}>
              <h2 style={{ fontSize: 20, marginBottom: 8, color: 'var(--text-primary)' }}>Reset Password</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 28 }}>
                Enter your email and we'll send you a link to reset your password.
              </p>
              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}
              {!success && (
                <form onSubmit={handleForgotPassword}>
                  <div className="form-group" style={{ marginBottom: 24 }}>
                    <label className="label">Email address</label>
                    <input type="email" className="input" placeholder="you@example.com"
                      value={email} onChange={e => setEmail(e.target.value)} required />
                  </div>
                  <button type="submit" className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 15 }}
                    disabled={submitting}>
                    {submitting ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}
            </div>
            <p style={{ textAlign: 'center', marginTop: 24 }}>
              <span
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer' }}
              >
                ← Back to sign in
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
